import React, { useState, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

export interface ConfirmableActionProps {
  /** Icon to show in the default (non-confirming) state */
  icon: React.ReactNode;
  /** Text or function returning text for the confirmation button */
  confirmText: string | (() => string);
  /** Called when user confirms the action */
  onConfirm: () => void;
  /** CSS class for the icon button (default state) */
  iconClassName?: string;
  /** CSS class for the confirmation button */
  confirmClassName?: string;
  /** Timeout in ms before confirmation resets (default: 3000) */
  timeout?: number;
  /** Tooltip text for the icon button (uses shadcn Tooltip) */
  tooltip?: string;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Called when confirmation state changes (for external reset triggers) */
  onConfirmingChange?: (confirming: boolean) => void;
  /** External control to reset confirmation */
  resetTrigger?: unknown;
}

/**
 * A button that requires confirmation before executing an action.
 *
 * Flow:
 * 1. Shows icon button (compact)
 * 2. On click → Shows confirmation button with text (wider, pushes siblings)
 * 3. On confirm click → Executes action, resets to icon
 * 4. Auto-resets after timeout if not confirmed
 */
const ConfirmableAction: React.FC<ConfirmableActionProps> = ({
  icon,
  confirmText,
  onConfirm,
  iconClassName = "",
  confirmClassName = "",
  timeout = 3000,
  tooltip,
  disabled = false,
  onConfirmingChange,
  resetTrigger,
}) => {
  const [confirming, setConfirming] = useState(false);

  // Reset confirmation when resetTrigger changes (e.g., selection changes)
  useEffect(() => {
    setConfirming(false);
  }, [resetTrigger]);

  // Notify parent of confirmation state changes
  useEffect(() => {
    onConfirmingChange?.(confirming);
  }, [confirming, onConfirmingChange]);

  // Auto-reset after timeout
  useEffect(() => {
    if (confirming && timeout > 0) {
      const timer = setTimeout(() => setConfirming(false), timeout);
      return () => clearTimeout(timer);
    }
  }, [confirming, timeout]);

  const resolvedConfirmText = typeof confirmText === "function" ? confirmText() : confirmText;

  if (confirming) {
    return (
      <Button
        variant="outline"
        className={confirmClassName}
        onClick={() => {
          onConfirm();
          setConfirming(false);
        }}
      >
        {resolvedConfirmText}
      </Button>
    );
  }

  const button = (
    <Button
      variant="outline"
      size="icon"
      disabled={disabled}
      className={iconClassName}
      onClick={() => setConfirming(true)}
    >
      {icon}
    </Button>
  );

  // Wrap in Tooltip if tooltip text is provided
  // Use span wrapper so tooltip works even when button is disabled
  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{button}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
};

export default ConfirmableAction;
